<template>
  <div class="d-flex flex-column gap-4">
    <div class="d-flex flex-row justify-space-between align-center mt-2">
      <p v-html="header"></p>
      <v-btn
        variant="outlined"
        size="small"
        data-testid="custom-forms-loadtype-forms-add"
        @click.stop="isCreateLoadtypeTriggerDialogVisible = true">
        <v-icon class="mr-1" size="small">mdi-plus</v-icon>
        {{ $t('customForms.buttons.newForm') }}
      </v-btn>
    </div>
    <p
      class="font-size-x-small"
      v-if="Boolean(featureData.subtitle)"
      v-html="featureData.subtitle"></p>

    <template v-if="hasLoadTypeForms && mainTriggers.length > 0">
      <v-expansion-panels class="my-4" elevation="0" flat static readonly>
        <v-expansion-panel
          class="border border-radius mb-2"
          v-for="{ form, trigger, flow } of mainTriggers"
          :key="form.id">
          <v-expansion-panel-title
            @click="$emit('manage', trigger)"
            hide-actions
            :data-testid="`custom-forms-loadtype-forms-edit-${form.name}`">
            <div class="typography-body-md">
              <v-icon size="x-small" class="mr-1">mdi-text-box-edit-outline</v-icon>
              <strong>{{ form.name }}</strong>

              <p class="typography-body-sm mt-2">
                <strong>Requested for:</strong>
                {{ getFormLoadTypesNames(flow.parentCode) }}
              </p>

              <div class="d-flex flex-row gap-2 mt-4">
                <v-chip
                  size="small"
                  color="grey-darken-1"
                  variant="outlined"
                  v-for="data of formData.filter(fd => fd.flow.parentCode === flow.parentCode)"
                  :key="data.form.id">
                  <span>{{ $t(`customForms.enums.${camelCase(data.trigger.app)}`) }}</span>
                  <v-icon
                    class="ml-2"
                    :title="
                      data.trigger.isActive
                        ? $t('customForms.messages.formActive')
                        : $t('customForms.messages.formNotVisible')
                    "
                    :color="data.trigger.isActive ? 'success' : 'grey'"
                    size="x-small">
                    mdi-circle
                  </v-icon>
                </v-chip>
              </div>
            </div>

            <v-icon size="small">mdi-chevron-right</v-icon>
          </v-expansion-panel-title>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
    <template v-else>
      <p
        class="typography-body-sm text--color-text-tertiary my-4"
        data-testid="custom-forms-loadtype-form-empty">
        {{ $t('customForms.messages.noFormsHaveBeenCreatedYet') }}
      </p>
    </template>

    <custom-forms-create-load-type-trigger-dialog
      :warehouse="warehouse"
      @form-created="handleFormCreated"
      @close="isCreateLoadtypeTriggerDialogVisible = false"
      :is-visible="isCreateLoadtypeTriggerDialogVisible" />
  </div>
</template>

<script>
import {
  CustomFormsFeatures,
  AppsEnum,
  getLoadTypesNames,
  getTranslatedCustomFormsFeaturesData
} from '@nova/core';
import { computed, ref } from 'vue';
import CustomFormsCreateLoadTypeTriggerDialog from './CustomFormsCreateLoadTypeTriggerDialog.vue';
import { camelCase } from 'lodash';

export default {
  methods: { camelCase },
  props: {
    triggers: {
      type: Array,
      default: () => []
    },
    warehouse: {
      type: Object,
      required: true
    }
  },
  emits: ['form-created', 'manage'],
  components: { CustomFormsCreateLoadTypeTriggerDialog },
  setup(props, { emit }) {
    const featureData = computed(
      () => getTranslatedCustomFormsFeaturesData()[CustomFormsFeatures.BookLoadType]
    );
    const isCreateLoadtypeTriggerDialogVisible = ref(false);

    const header = computed(() => `${featureData.value.description} ${featureData.value.helpLink}`);

    const hasLoadTypeForms = computed(
      () =>
        props.triggers?.length > 0 &&
        props.triggers.every(t => t.feature === CustomFormsFeatures.BookLoadType)
    );

    const formData = computed(() => {
      const data = [];

      // Group triggers by flow.parentCode
      const flowGroups = {};

      props.triggers?.forEach(trigger => {
        const flow = trigger.flow;
        const form = flow?.formFrom;

        if (!flow || !form) {
          return;
        }

        const key = flow.id;

        if (!flowGroups[key]) {
          flowGroups[key] = { form, flow, trigger };
        }
      });

      // Convert the grouped data back to array
      data.push(...Object.values(flowGroups));

      return data.sort((a, b) => {
        // First sort by trigger.app
        if (a.trigger.app !== b.trigger.app) {
          return a.trigger.app.localeCompare(b.trigger.app);
        }
        // Then sort by form.name
        return a.form.name.localeCompare(b.form.name);
      });
    });

    const mainTriggers = computed(() =>
      formData.value.filter(fd => fd.trigger?.app === AppsEnum.All)
    );

    const handleFormCreated = async data => {
      emit('form-created', data);
    };

    const getFormLoadTypesNames = parentCode => {
      const loadTypes = (props.triggers ?? [])
        .filter(t => t.flow?.parentCode === parentCode)
        .map(t => t.loadType);

      return getLoadTypesNames(loadTypes);
    };

    return {
      handleFormCreated,
      featureData,
      header,
      AppsEnum,
      hasLoadTypeForms,
      formData,
      mainTriggers,
      getFormLoadTypesNames,
      isCreateLoadtypeTriggerDialogVisible
    };
  }
};
</script>
